import { SUPPORT_CONTACT_LINE, SUPPORT_HELPLINE, SUPPORT_REPLY_WINDOW } from '../config';
import { PERSONALITIES, Personality, pickOne, withName } from './personalities';

export { pickOne, withName, DEFAULT_PERSONALITY, PERSONALITIES, personalityById, pickPersonality } from './personalities';
export type { Personality } from './personalities';

/* ---- Voice beats: wording comes from the session's personality (see personalities.ts) ---- */

type Pick = () => number;
const every = <K extends keyof Personality>(key: K): Personality[K] extends ReadonlyArray<infer T> ? readonly T[] : never =>
  PERSONALITIES.flatMap((p) => p[key] as readonly unknown[]) as never;

export function randomGreeting(p: Personality, firstName: string, pick?: Pick): string {
  return pickOne(p.greetings, pick)(firstName || 'there');
}
/** Every greeting across all voices; handy for tests and transcript checks. */
export const GREETING_VARIANTS = every('greetings');

export const MENU_INTRO_VARIANTS = every('menuIntros');
export const menuIntro = (p: Personality, pick?: Pick) => pickOne(p.menuIntros, pick);

export const FALLBACK_VARIANTS = every('fallbacks');
export const fallback = (p: Personality, pick?: Pick) => pickOne(p.fallbacks, pick);

export const THANKS_VARIANTS = every('thanks');
export const thanks = (p: Personality, firstName: string, pick?: Pick) => pickOne(p.thanks, pick)(firstName);

export const NO_CASES_VARIANTS = every('noCases');
export const noCases = (p: Personality, pick?: Pick) => pickOne(p.noCases, pick);

export const CASES_ERROR_VARIANTS = every('casesError');
export const casesError = (p: Personality, pick?: Pick) => pickOne(p.casesError, pick);

export const CASE_NOT_FOUND_VARIANTS = every('caseNotFound');
export const caseNotFound = (p: Personality, refNo: string, pick?: Pick) => pickOne(p.caseNotFound, pick)(refNo);

export const SINGLE_CASE_INTRO_VARIANTS = every('singleCaseIntros');
export const singleCaseIntro = (p: Personality, firstName: string, pick?: Pick) => pickOne(p.singleCaseIntros, pick)(firstName);

export const WHICH_CASE_VARIANTS = every('whichCase');
export const whichCase = (p: Personality, pick?: Pick) => pickOne(p.whichCase, pick);

export const NOTHING_FOR_APPROVAL_VARIANTS = every('nothingForApproval');
export const nothingForApproval = (p: Personality, pick?: Pick) => pickOne(p.nothingForApproval, pick);

export const NOTHING_IN_PROGRESS_VARIANTS = every('nothingInProgress');
export const nothingInProgress = (p: Personality, pick?: Pick) => pickOne(p.nothingInProgress, pick);

export const FOR_APPROVAL_INTRO_VARIANTS = every('forApprovalIntros');
export const forApprovalIntro = (p: Personality, pick?: Pick) => pickOne(p.forApprovalIntros, pick);

export const IN_PROGRESS_INTRO_VARIANTS = every('inProgressIntros');
export const inProgressIntro = (p: Personality, pick?: Pick) => pickOne(p.inProgressIntros, pick);


export const HOW_TO_TRACK = [
  'Easiest way is the **My Cases** page:',
  '1. Open [My Cases](/cases).',
  '2. Search by patient name or case number.',
  '3. Click a case to see its files, chat and where it is right now.',
  '',
  "Or just drop a case number in here and I'll pull it up for you.",
].join('\n');

export const REPORT_PROBLEM_PROMPT_VARIANTS = every('reportProblemPrompts');
export const reportProblemPrompt = (p: Personality, pick?: Pick) => pickOne(p.reportProblemPrompts, pick);

export const TRIAGE_RECORDED = (email: string, ticketRef?: string) =>
  `Got it, thanks. I've passed this on to our support team${ticketRef ? ` (ref **${ticketRef}**)` : ''} and they'll reply to you at **${email}**.`;
export const TALK_TO_HUMAN_RECORDED = (email: string, ticketRef?: string) =>
  `No problem. I've asked someone from the team to reach out to you at **${email}**${ticketRef ? ` (ref **${ticketRef}**)` : ''}. They'll have this chat for context.`;
/** Used when triage is accepted locally but there is no endpoint to deliver it to. */
export const TRIAGE_NOTED =
  "Thanks, I've noted that. I can't raise a ticket from this environment, so please email **support@evidentdigital.com** with the case number and the team will sort it out.";
export const TRIAGE_UNAVAILABLE = `I couldn't get that through to the team just now, sorry. Try again in a bit. ${SUPPORT_CONTACT_LINE}`;

/* ---- Escalation: unanswered questions, design-team hand-off, support call-backs ---- */

export const UNRESOLVED_WITH_CASE_VARIANTS = [
  "I couldn't answer that one, sorry. If it's about this case, the design team can help from the case page. Otherwise I can have our support team reach out to you.",
  "That one's beyond me, sorry. For anything about this case, the design team is your best bet. For anything else, I can ask support to get in touch.",
] as const;
export const UNRESOLVED_NO_CASE_VARIANTS = [
  "I couldn't answer that one, sorry. I can have our support team reach out to you, or if it's about a case, tell me the case number and I'll point you to the right people.",
  "That one's beyond me, sorry. I can ask our support team to get in touch, or pop a case number in and I'll find the right people for it.",
] as const;
export const unresolvedPrompt = (hasCase: boolean, pick?: () => number) =>
  pickOne(hasCase ? UNRESOLVED_WITH_CASE_VARIANTS : UNRESOLVED_NO_CASE_VARIANTS, pick);

export const WHICH_CASE_IS_SLOW_VARIANTS = every('whichCaseIsSlow');
export const whichCaseIsSlow = (p: Personality, pick?: Pick) => pickOne(p.whichCaseIsSlow, pick);

export const DESIGN_TEAM_HANDOFF = (caseRefNo: string, link: string | null) =>
  [
    `The design team working on case **${caseRefNo}** is the best place for this. They read and reply to messages on the case page, so you'll get an answer from the people actually handling it.`,
    link ? `[Open the Messages tab for case ${caseRefNo}](${link})` : 'Open the case from **My Cases** and use the **Messages** tab.',
    "If it's about something other than the case itself, I can have our support team reach out instead.",
  ].join('\n\n');

/* ---- Case questions that are not about status ---- */

const messagesLink = (caseRefNo: string, link: string | null) =>
  link ? `[Open the Messages tab for case ${caseRefNo}](${link})` : 'Open the case from **My Cases** and use the **Messages** tab.';

const quoted = (text: string) => text.split('\n').map((line) => `> ${line}`).join('\n');

/** Prefix on a message Evie posts to a case, so the design team sees it is a request relayed from the chat. */
export const CASE_MESSAGE_PREFIX = '[Via Evie – change request]';

export const CASE_QUESTION_CONFIRM = (caseRefNo: string, text: string) =>
  [
    `I can't make changes to case **${caseRefNo}** from here, but the design team handling it can. Shall I send them this on the case's Messages tab?`,
    quoted(text),
    "I've also let our support team know.",
  ].join('\n\n');

export const CASE_QUESTION_REWORDED = (caseRefNo: string, text: string) =>
  [`Send this to the design team on case **${caseRefNo}** instead?`, quoted(text)].join('\n\n');

export const CASE_MESSAGE_SENT = (caseRefNo: string, link: string | null) =>
  [`Sent. The design team on case **${caseRefNo}** will reply on the case page.`, messagesLink(caseRefNo, link)].join('\n\n');

export const CASE_MESSAGE_FAILED = (caseRefNo: string, link: string | null) =>
  [`Sorry, I couldn't send that to case **${caseRefNo}**. Please post it on the Messages tab yourself:`, messagesLink(caseRefNo, link)].join('\n\n');

export const CASE_MESSAGE_SKIPPED = (caseRefNo: string, link: string | null) =>
  ['No problem, I won\'t send it. You can message the design team yourself whenever you like:', messagesLink(caseRefNo, link)].join('\n\n');

export const WHICH_CASE_FOR_CONTACT_VARIANTS = [
  "Is this about a case? Pick one below and I'll point you to its design team. If it's about something else, I can have support reach out to you.",
  'If this is about a case, choose it below and I\'ll send you to the right team. For anything else, I can ask support to get in touch.',
] as const;
export const whichCaseForContact = (pick?: () => number) => pickOne(WHICH_CASE_FOR_CONTACT_VARIANTS, pick);

export const ASK_SUMMARY_VARIANTS = every('askSummary');
export const askSummaryPrompt = (p: Personality, pick?: Pick) => pickOne(p.askSummary, pick);

/* ---- Hand-off to support: where and when they will hear back ---- */

const withRef = (ticketRef?: string) => (ticketRef ? ` (ref **${ticketRef}**)` : '');

/** The one-line hand-off: the ticket is already filed with this email; the reply comes by email, not here. */
export const HANDOFF_EMAIL = (email: string, ticketRef?: string) =>
  `I'll pass this to our support team${withRef(ticketRef)}. They'll email you at **${email}** within ${SUPPORT_REPLY_WINDOW}.`;
export const HANDOFF_PHONE = (phone: string, ticketRef?: string) =>
  `Done${withRef(ticketRef)}. Our support team will call you on **${phone}** within ${SUPPORT_REPLY_WINDOW}.`;
export const CONTACT_UPDATED_EMAIL = (email: string) =>
  `Updated. They'll email you at **${email}** instead, within ${SUPPORT_REPLY_WINDOW}.`;

export const USE_DIFFERENT_EMAIL_LABEL = 'Use a different email';
export const CALL_ME_LABEL = 'Call me instead';

export const ASK_DIFFERENT_EMAIL = 'Sure. What email address should they use?';
export const ASK_PHONE = 'Sure. What number should they call?';
export const INVALID_EMAIL = "I didn't spot an email address in that. Could you type one in, for example **name@practice.com**?";
export const INVALID_PHONE = "I didn't spot a phone number in that. Could you type one in, for example **0400 123 456**?";
/** After repeated unusable answers the ticket keeps the email it was filed with. */
export const KEEPING_ACCOUNT_EMAIL = (email: string) => `No worries, I'll leave it with **${email}**; the team will email you there within ${SUPPORT_REPLY_WINDOW}.`;

/** Only when the session has no account email: support still replies by email or phone, never in this chat. */
export const askContactPrompt = () =>
  'Thanks. Support will reply by email or phone rather than in this chat, so where should they reach you? Reply with an email address or phone number.';

export const INVALID_CONTACT = "I didn't spot an email address or phone number in that. Could you type one in, for example **name@practice.com** or **0400 123 456**?";

/* ---- Emergency: two ways to reach a person right now ---- */

/** Case Evie can point the dentist at, or null when none is known. */
export interface EmergencyCase {
  caseRefNo: string;
  link: string | null;
}

export const EMERGENCY_HELPLINE_LINK = `[${SUPPORT_HELPLINE.display}](tel:${SUPPORT_HELPLINE.tel})`;

/**
 * The first bubble on every emergency, one fixed wording: the case's Messages tab (when a case is
 * known) and the helpline, shown in full and dialable. The hand-off line follows in its own bubble.
 */
export const EMERGENCY_REPLY = (c: EmergencyCase | null) =>
  [
    "Understood, I'll treat this as urgent. Two ways to reach a person right now:",
    c
      ? `1. **Message the team on case ${c.caseRefNo}**: ${c.link ? `[open its Messages tab](${c.link})` : 'open the case from **My Cases** and use the **Messages** tab'}. The people handling the case read those first.`
      : "1. **Message the team on the case**: tap the case below, or tell me the case number, and I'll link you straight to its Messages tab.",
    `2. **Call our helpline on ${EMERGENCY_HELPLINE_LINK}**.`,
  ].join('\n\n');

/* ---- Escalation gating and de-escalation ---- */

export const BEFORE_SUPPORT_VARIANTS = [
  "Happy to get support involved. Before I do, let me see if I can sort it faster. Is it about one of these cases, or something else?",
  "I can arrange a call-back, but I might be able to help right now. Pick a case below, or tell me what's going on and I'll do my best.",
] as const;
export const beforeSupportPrompt = (pick?: () => number) => pickOne(BEFORE_SUPPORT_VARIANTS, pick);

export const CALMING_ACK_VARIANTS = [
  (firstName: string) => `I'm sorry${withName(firstName)}. I can hear this has been frustrating, and that's fair. Let's get it sorted.`,
  (firstName: string) => `That's not the experience you should be having${withName(firstName)}, and I'm sorry. Let me see what I can do right now.`,
  (firstName: string) => `Understood${withName(firstName)}, and I'm sorry for the hassle. Here's what I can tell you straight away.`,
] as const;
export const calmingAck = (firstName: string, pick?: () => number) => pickOne(CALMING_ACK_VARIANTS, pick)(firstName);

export const CALMING_DEFLECT_VARIANTS = [
  "I'm sorry this has been a hassle. I can get support on the phone to you, and I'll flag it as urgent. Before that, if it's about one of these cases I can give you an answer right now:",
  "Sorry, I know this isn't what you wanted to hear from a chatbot. I'll flag it as urgent for support if you'd like, but if it's one of these cases I can look into it straight away:",
] as const;
export const calmingDeflect = (pick?: () => number) => pickOne(CALMING_DEFLECT_VARIANTS, pick);

// First ask for a person: nothing has gone wrong yet, so just agree. Nobody can join this chat, so the
// wording promises a follow-up from a person, never a transfer or someone coming online here.
export const ESCALATION_FIRST_ASK_VARIANTS = [
  "Of course. A person can't take over this chat, but I'll have one follow up with you.",
  "Sure thing. Nobody can join this chat, so I'll get a person to follow up with you instead.",
] as const;
// Two or more frustrated turns in a row: own the loop before handing off.
export const ESCALATION_CIRCLING_VARIANTS = [
  "I understand. I'll stop going round in circles and have a person follow up with you.",
  "You've been patient enough. Let me get the team to reach out to you, flagged as urgent.",
] as const;
/** Every acknowledgement Evie can open an escalation with; handy for tests and transcript checks. */
export const ESCALATION_ACK_VARIANTS = [...ESCALATION_FIRST_ASK_VARIANTS, ...ESCALATION_CIRCLING_VARIANTS] as const;
export const escalationAck = (circling: boolean, pick?: () => number) =>
  pickOne(circling ? ESCALATION_CIRCLING_VARIANTS : ESCALATION_FIRST_ASK_VARIANTS, pick);

export const CONTACT_CANCELLED_VARIANTS = every('contactCancelled');
export const contactCancelled = (p: Personality, pick?: Pick) => pickOne(p.contactCancelled, pick);

/* ---- Ending the chat ---- */

export const GOODBYE_VARIANTS = every('goodbyes');
export const goodbye = (p: Personality, firstName: string, pick?: Pick) => pickOne(p.goodbyes, pick)(firstName);
